import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { LOGOUT } from "@/constants/testIds";
import { LanguageSelector } from "@/components/hud/LanguageSelector";

const LINKS = [
  { to: "/create", label: "nav.create", id: "create", cls: "nav-cta-eublue" },
  { to: "/montage", label: "nav.montage", id: "montage", cls: "nav-cta-green" },
  { to: "/music", label: "nav.music", id: "music", cls: "nav-cta-fuchsia" },
  { to: "/billing", label: "nav.premium", id: "premium" },
];

// Navbar: original neon logo only (left) | flexible space | navigation + controls (right).
export function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-0 z-40 border-b border-[#2E7BFF]/15 bg-[linear-gradient(180deg,rgba(7,11,22,0.82),rgba(5,7,13,0.55))] shadow-[0_10px_40px_-20px_rgba(0,0,0,0.9)] backdrop-blur-2xl"
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#4d8fff]/70 to-transparent shadow-[0_0_14px_rgba(46,123,255,0.5)]" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="mx-auto flex h-[68px] max-w-7xl items-center px-5 sm:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-3 py-2 pr-6" data-testid="navbar-logo" aria-label="Simulation Studios">
          <img
            src="/brand/logo-simulation-studios.jpeg"
            alt="Simulation Studios"
            className="logo-neon h-11 w-11 object-contain transition-[transform,filter] duration-300 hover:scale-105 hover:[filter:drop-shadow(0_0_16px_rgba(46,123,255,0.8))]"
          />
          <span data-testid="navbar-home-label" className="text-sm font-semibold uppercase tracking-[0.22em] text-white">
            {t("nav.home")}
          </span>
        </Link>

        <div className="flex-1" />

        <div className="flex items-center gap-2 sm:gap-3">
          {(
            <div className="hidden items-center gap-2 md:flex">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={cn("nav-cta", l.cls, isActive(l.to) && "nav-cta-active")}
                  data-testid={`navbar-link-${l.id}`}
                >
                  {t(l.label)}
                </Link>
              ))}
              <a
                href="https://simulatoretemporale.com"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-cta nav-cta-cyan"
                data-testid="navbar-link-whatif"
              >
                WHAT-IF
              </a>
              <span className="mx-3 h-5 w-px bg-gradient-to-b from-transparent via-[#2E7BFF]/40 to-transparent" />
            </div>
          )}

          <LanguageSelector />

          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="navbar-projects-btn"
                title={t("nav.myProjects")}
                aria-label={t("nav.myProjects")}
                className={cn("nav-cta nav-cta-green-light !w-9 !px-0", isActive("/dashboard") && "nav-cta-active")}
              >
                <Video className="h-4 w-4" strokeWidth={2} />
              </Link>
              <button onClick={doLogout} data-testid={LOGOUT.button} title={t("common.logout")} className="nav-icon">
                <LogOut className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="navbar-login-link" className="nav-link text-[#2E7BFF]">
              {t("common.login")}
            </Link>
          )}
        </div>
      </div>
      {(
        <div className="grid grid-cols-5 gap-2 px-4 pb-3 md:hidden" data-testid="navbar-mobile-nav">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={cn("nav-cta !h-9 !px-2 !text-[11px]", l.cls, l.id === "premium" && "!px-1 !text-[9px] !tracking-[0.03em]", isActive(l.to) && "nav-cta-active")}
              data-testid={`navbar-mobile-link-${l.id}`}
            >
              {t(l.label)}
            </Link>
          ))}
          <a
            href="https://simulatoretemporale.com"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-cta nav-cta-cyan !h-9 !px-2 !text-[11px]"
            data-testid="navbar-mobile-link-whatif"
          >
            WHAT-IF
          </a>
        </div>
      )}
    </nav>
  );
}
